import { t } from "@hakili/i18n";
import { Button, Callout, Input, Modal } from "@hakili/ui";
import { AlertTriangle } from "lucide-react";

import { fill } from "./dossiers.js";
import { GuardedAct } from "./guarded-act.js";
import { ModalCancel } from "./modal-cancel.js";
import { readReturnDay } from "../return-day.js";

/**
 * `EQUIPE-MODALS` (D2 frames 82 and 84; the architect's go after R6): **the row's two destructive or quiet-but-binding acts get their
 * step.** The absence was an inline form and the removal was one click on the row that deactivated; both are dialogs now, in the order and
 * the width of every bank dialog (« Annuler » first, the action dark and last, no ✕, 640 px). Both call the doors that already exist
 * (`PATCH /users/{id}/availability`, `POST /users/{id}/deactivate`) and add no door.
 */

/** Frame 82, « Déclarer une absence »: the return date is the door's own requirement, said beside the guarded confirm until it is typed. */
export function AbsenceDialog(props: {
  readonly memberId: string;
  readonly memberName: string;
  /** What she typed, as she typed it (« 28 octobre », « 28/10 », « 28/10/2026 »). */
  readonly text: string;
  /** The door's own day, what a day with no year is read against. */
  readonly today: string | null;
  readonly onText: (value: string) => void;
  readonly busy: boolean;
  /** Called with the day the door takes, `YYYY-MM-DD`. */
  readonly onConfirm: (day: string) => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const day = readReturnDay(props.text, props.today);
  return (
    <Modal
      open
      title={t("bank.equipe.declarerAbsence")}
      onClose={props.onClose}
      cross={false}
      size="lg"
      actions={
        <>
          <ModalCancel
            testId={`equipe-absence-annuler-${props.memberId}`}
            focusOnOpen
            onClick={props.onClose}
          />
          {day === null ? (
            <GuardedAct
              testId={`equipe-confirmer-absence-${props.memberId}`}
              reasonId={`equipe-absence-raison-${props.memberId}`}
              reason={
                props.text.trim() === ""
                  ? t("bank.equipe.confirmerAbsenceMotif")
                  : t("bank.equipe.retourIllisible")
              }
            >
              {t("bank.equipe.confirmerAbsence")}
            </GuardedAct>
          ) : (
            <Button
              variant="ink"
              disabled={props.busy}
              data-testid={`equipe-confirmer-absence-${props.memberId}`}
              onClick={() => {
                props.onConfirm(day);
              }}
            >
              {t("bank.equipe.confirmerAbsence")}
            </Button>
          )}
        </>
      }
    >
      <div data-testid={`equipe-absence-form-${props.memberId}`}>
        <p className="mb-3 text-sm leading-relaxed text-gray-500">
          {fill("bank.equipe.absencePhraseD2", { nom: props.memberName })}
        </p>
        <label
          className="mb-1.5 block text-sm font-medium text-gray-700"
          htmlFor={`equipe-retour-${props.memberId}`}
        >
          {t("bank.equipe.retourLe")}
        </label>
        <div className="w-56">
          <Input
            id={`equipe-retour-${props.memberId}`}
            testId={`equipe-retour-${props.memberId}`}
            type="text"
            value={props.text}
            onChange={props.onText}
            placeholder={t("bank.equipe.retourExemple")}
          />
        </div>
      </div>
    </Modal>
  );
}

const exitBlocked = (files: number): string =>
  files === 1 ? t("bank.equipe.sortieUn") : fill("bank.equipe.sortiePlusieurs", { n: String(files) });

/**
 * Frame 84, « Retirer {nom} de l'équipe ». **The count comes from the row** (`openFiles`). Files remain: « Annuler » and « Transférer tout
 * son portefeuille », and the door is NEVER called. None: « Annuler » and the dark « Retirer de l'équipe », which calls the door; if the door
 * still refuses (a stale count) its own sentence is shown here and the dialog stays open.
 */
export function RemoveDialog(props: {
  readonly memberId: string;
  readonly memberName: string;
  readonly openFiles: number;
  readonly busy: boolean;
  /** The door's refusal for THIS member after the press, in its own words; null before the press or when it accepted. */
  readonly refusal: string | null;
  /** Undefined where the transfer doors are not handed: the control is then not drawn (D-1019-2). */
  readonly onTransfer: (() => void) | undefined;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const blocked = props.openFiles > 0;
  return (
    <Modal
      open
      title={fill("bank.equipe.retirerTitre", { nom: props.memberName })}
      onClose={props.onClose}
      cross={false}
      size="lg"
      actions={
        <>
          <ModalCancel
            testId={`equipe-retirer-annuler-${props.memberId}`}
            focusOnOpen
            onClick={props.onClose}
          />
          {blocked ? (
            props.onTransfer === undefined ? null : (
              <Button
                variant="secondary"
                data-testid={`equipe-retirer-transferer-${props.memberId}`}
                onClick={props.onTransfer}
              >
                {t("bank.equipe.transfererTout")}
              </Button>
            )
          ) : (
            <Button
              variant="ink"
              disabled={props.busy}
              data-testid={`equipe-retirer-confirmer-${props.memberId}`}
              onClick={props.onConfirm}
            >
              {t("bank.equipe.retirer")}
            </Button>
          )}
        </>
      }
    >
      <div data-testid={`equipe-retirer-dialogue-${props.memberId}`}>
        {blocked ? (
          <Callout
            tone="bad"
            className="mb-3"
            icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
          >
            <p className="font-semibold">{exitBlocked(props.openFiles)}</p>
            <p>{t("bank.equipe.retirerPhraseAvec")}</p>
          </Callout>
        ) : (
          <p className="mb-3 text-sm text-gray-500">{t("bank.equipe.retirerPhraseSans")}</p>
        )}
        {props.refusal === null ? null : (
          <Callout
            tone="bad"
            className="mb-3"
            testId={`equipe-retirer-refus-${props.memberId}`}
            icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
          >
            {props.refusal}
          </Callout>
        )}
      </div>
    </Modal>
  );
}
